import type {
  ReportInterviewQuestion,
  ReportSupportingResponse,
} from "./experimentReport";
import type {
  ExperimentDetail,
  ExperimentInsightsData,
} from "../api/experimentsApi";

// Hardcoded report content for the demo experiments. Returned only when the
// experiment name matches and the real insights payload is missing or not in
// a generated state — see ExperimentReportPage for the gating.

const CLINICAL_INTAKE_NAME = "Clinical Intake Form Simplification";
const CREDIT_CARD_NAME = "Credit Card Application Streamlining Experiment";

const normalizeName = (value?: string | null): string =>
  (value ?? "").trim().toLowerCase().replace(/\s+/g, " ");

export interface SyntheticParticipantRow {
  id: string;
  name: string;
  age: number;
  /** Free-form segment label (e.g. "New Patient", "Existing Customer"). */
  visit_type: string;
  device: "Mobile" | "Desktop";
  /** Free-form value for the rightmost-of-the-numeric columns. For some
   *  experiments this is a duration ("2m 34s"), for others an outcome
   *  ("Activated", "Abandoned at Step 2"). */
  completion_time: string;
  sentiment: "positive" | "neutral" | "negative";
  quote: string;
}

export interface SyntheticInsightsOverride {
  insightsData: ExperimentInsightsData;
  metricsOverride: {
    completion_rate: number | null;
    average_time_to_complete: string | null;
  };
  decisionSupportingResponses: ReportSupportingResponse[];
  interviewQuestionsByRqId: Record<string, ReportInterviewQuestion[]>;
  participants: SyntheticParticipantRow[];
  /** Header label for the segment column (default "Visit Type"). */
  participantSegmentLabel?: string;
  /** Header label for the time/outcome column (default "Time"). */
  participantTimeLabel?: string;
  /** Optional experiment metadata fallback used when the experiment does not
   *  exist in the live API (e.g. a fully-synthetic demo experiment). When the
   *  real experiment query returns data, this field is ignored. */
  experimentDetailFallback?: ExperimentDetail;
}

export const getSyntheticInsightsForExperiment = (
  experimentName?: string | null,
): SyntheticInsightsOverride | null => {
  const normalized = normalizeName(experimentName);

  if (normalized === normalizeName(CLINICAL_INTAKE_NAME)) {
    return buildClinicalIntakeOverride();
  }

  if (normalized === normalizeName(CREDIT_CARD_NAME)) {
    return buildCreditCardStreamliningOverride();
  }

  return null;
};

function buildClinicalIntakeOverride(): SyntheticInsightsOverride {
  const timestamp = new Date().toISOString();

  const insightsData: ExperimentInsightsData = {
    id: "synthetic-clinical-intake",
    experiment_id: "synthetic-clinical-intake",
    analysis_run_id: "synthetic-run-1",
    status: "generated",
    timestamp,
    analysis_summary: {
      participant_count: 42,
      question_coverage: 1,
      free_text_response_rate: 0.83,
      eligible_rq_count: 3,
      insufficient_evidence_rq_count: 0,
    },
    final_decision: {
      decision_label: "pursue",
      pursue_probability: 0.87,
      pivot_probability: 0.09,
      pause_probability: 0.04,
      confidence_bucket: "high",
      impact_score: 8.7,
      verbalized_decision:
        "Strong, consistent evidence across mobile and desktop participants supports rolling out the simplified clinical intake. Completion lifted by 34% versus the legacy 18-field form, drop-off fell from 41% to 9%, and qualitative feedback from first-time and walk-in patients was overwhelmingly positive on perceived effort, trust, and clarity.",
      rationale:
        "Three out of three core research questions met the success threshold with high confidence. Auto-fill, step-by-step progression, and reduced mandatory fields each produced statistically meaningful gains, with no measurable increase in data-quality issues.",
      recommended_actions: [
        "Promote the simplified intake to default for all walk-in and first-time-patient flows in the next release window.",
        "Keep the optional 'main concern' free-text field — 71% of patients used it and triage staff cited it as the most useful single input.",
        "Add a lightweight insurance-card OCR step in a follow-up iteration; 4 participants flagged Member ID lookup as the remaining friction point.",
        "Instrument per-step drop-off telemetry before broader rollout so we can monitor regressions across clinics.",
      ],
    },
    core_research_question_results: [
      {
        rq_id: "rq-autofill",
        research_question:
          "Does auto-fill (name, age, contact) reduce user effort?",
        answer_label: "supported",
        positive_posterior: 0.93,
        negative_posterior: 0.04,
        certainty: 0.93,
        evidence_quality: 0.88,
        confidence_score: 0.91,
        confidence_bucket: "high",
        status: "answered",
        verbalized_answer:
          "Yes — auto-fill cut the median time to complete Step 1 (Welcome & Basic Info) from 71s to 19s, and 37 of 42 participants explicitly described the form as 'easy' or 'effortless'. Returning users with stored profiles completed the entire intake in under 90 seconds.",
        rationale:
          "Effort scores (1–7 Likert) averaged 6.4 with auto-fill on versus 4.1 in the control. Touch-event telemetry showed a 62% reduction in keystrokes per session. No participant in the auto-fill arm corrected a pre-filled field incorrectly, suggesting accuracy was preserved.",
        observed_factors: [
          {
            factor_id: "device-type",
            factor_name: "Device Type",
            top_value: "Mobile",
            top_value_posterior: 0.74,
          },
          {
            factor_id: "patient-type",
            factor_name: "Patient Type",
            top_value: "First-time patient",
            top_value_posterior: 0.62,
          },
        ],
        risk_flags: [
          {
            flag: "Edge case",
            severity: "low",
            detail:
              "2 participants on shared family devices saw another household member's pre-filled details. Consider an explicit 'Is this you?' confirmation step.",
          },
        ],
        evidence_trace: [
          {
            participant_id: "p-08",
            participant_name: "Maya R.",
            exemplar_quotes: [
              "It already knew my name and birthday — I just had to confirm. That felt really respectful of my time.",
              "On my phone in the waiting room, this took maybe 30 seconds. The old paperwork takes me ten minutes.",
            ],
          },
          {
            participant_id: "p-21",
            participant_name: "Daniel K.",
            exemplar_quotes: [
              "I almost missed the auto-filled phone number was wrong — would be nice if it highlighted what was pre-filled.",
            ],
          },
          {
            participant_id: "p-33",
            participant_name: "Priya S.",
            exemplar_quotes: [
              "Less typing on a small screen is huge. My thumbs thank you.",
            ],
          },
        ],
      },
      {
        rq_id: "rq-step-vs-single",
        research_question:
          "Does step-by-step vs single-page form perform better?",
        answer_label: "supported",
        positive_posterior: 0.88,
        negative_posterior: 0.07,
        certainty: 0.88,
        evidence_quality: 0.84,
        confidence_score: 0.86,
        confidence_bucket: "high",
        status: "answered",
        verbalized_answer:
          "Yes — the 4-step progressive form outperformed the single-page control on every measured dimension. Completion rose to 91% (vs. 67%), abandonment dropped 78%, and perceived effort improved by 1.9 points on a 7-point scale.",
        rationale:
          "Step-by-step layout reduced cognitive load: participants reported fewer 'where am I' moments and the visible progress bar was the most-cited positive feature in open-ended feedback. Single-page abandonment clustered at the medical-history section, which the stepped version isolates and hides behind toggles.",
        observed_factors: [
          {
            factor_id: "screen-size",
            factor_name: "Screen Size",
            top_value: "<= 6.1 inch",
            top_value_posterior: 0.69,
          },
          {
            factor_id: "step-completion",
            factor_name: "Highest-friction step",
            top_value: "Step 3 — Medical Overview",
            top_value_posterior: 0.41,
          },
        ],
        risk_flags: [
          {
            flag: "Returning users",
            severity: "low",
            detail:
              "3 returning patients preferred the single-page layout because they could scan-and-skip — consider an 'expert mode' toggle in a future iteration.",
          },
        ],
        evidence_trace: [
          {
            participant_id: "p-02",
            participant_name: "Aisha M.",
            exemplar_quotes: [
              "Seeing 'Step 2 of 4' made it feel finite. The old form felt like it would never end.",
              "I liked that medical questions only appeared if I said yes — I didn't have to look at allergy fields I don't need.",
            ],
          },
          {
            participant_id: "p-14",
            participant_name: "Tom B.",
            exemplar_quotes: [
              "Each screen had one job. I never wondered what to do next.",
            ],
          },
          {
            participant_id: "p-27",
            participant_name: "Lena O.",
            exemplar_quotes: [
              "The progress bar gave me confidence I was almost done. I would have given up on a wall of fields.",
            ],
          },
        ],
      },
      {
        rq_id: "rq-mandatory-fields",
        research_question:
          "Does reducing mandatory fields increase form completion rates?",
        answer_label: "supported",
        positive_posterior: 0.9,
        negative_posterior: 0.05,
        certainty: 0.9,
        evidence_quality: 0.86,
        confidence_score: 0.89,
        confidence_bucket: "high",
        status: "answered",
        verbalized_answer:
          "Yes — cutting mandatory fields from 18 to 7 increased completion from 67% to 91% with no measurable degradation in data sufficiency at the front desk. 71% of patients voluntarily completed at least one optional field, suggesting clinical detail isn't lost when fields are simply made optional.",
        rationale:
          "Drop-off heatmaps in the control concentrated at insurance and medical-history fields. In the simplified flow these became opt-in toggles, and 86% of participants who turned them on filled in meaningful detail. Front-desk staff in a parallel interview confirmed that the lighter form 'gives us what we need to triage' for the typical visit.",
        observed_factors: [
          {
            factor_id: "visit-type",
            factor_name: "Visit Type",
            top_value: "Walk-in Visit",
            top_value_posterior: 0.58,
          },
          {
            factor_id: "abandonment-cause",
            factor_name: "Top abandonment reason (control)",
            top_value: "Insurance Member ID lookup",
            top_value_posterior: 0.47,
          },
        ],
        risk_flags: [
          {
            flag: "Operational follow-up",
            severity: "low",
            detail:
              "Front-desk staff still need a way to request missing optional fields post-check-in. Recommend a follow-up tablet flow at the desk.",
          },
        ],
        evidence_trace: [
          {
            participant_id: "p-05",
            participant_name: "Jordan L.",
            exemplar_quotes: [
              "I was relieved I didn't have to dig out my insurance card before I'd even sat down.",
              "When it asked if I had allergies, I said yes and then it asked for details — that felt smart, not pushy.",
            ],
          },
          {
            participant_id: "p-19",
            participant_name: "Sam P.",
            exemplar_quotes: [
              "Honestly the old form had me convinced I was about to be billed for something. This one felt like a normal app.",
            ],
          },
          {
            participant_id: "p-44",
            participant_name: "Carla T.",
            exemplar_quotes: [
              "I appreciated that 'reason for visit' was free text — I could just say 'sore throat for 4 days' instead of guessing the right category.",
            ],
          },
        ],
      },
    ],
    general_insights: [
      {
        insight_id: "gi-mobile-first",
        title: "Mobile-first patients are the dominant intake channel",
        type: "behavioral",
        support_score: 0.94,
        confidence_bucket: "high",
        evidence: {
          summary:
            "74% of intake sessions were completed on mobile devices, and mobile completion rate (93%) actually exceeded desktop (87%). The large tap targets and single-column layout were repeatedly cited as the reason. Designing the form mobile-first paid off in the channel that matters most.",
          quotes: [
            "I filled this out in line at the pharmacy on my phone. That would not have been possible with the PDF.",
            "Buttons were big enough that I didn't accidentally tap the wrong thing — even with one hand.",
          ],
        },
      },
      {
        insight_id: "gi-trust-signals",
        title: "Tooltips on sensitive fields measurably increase trust",
        type: "qualitative",
        support_score: 0.81,
        confidence_bucket: "high",
        evidence: {
          summary:
            "The 'Why we need this' tooltips on date of birth and phone reduced abandonment at Step 1 by 23%. 5 of 42 participants spontaneously mentioned the tooltips as the moment they decided to keep going. This is a small UI primitive with outsized retention impact.",
          quotes: [
            "When I tapped the little (i) and it said 'we use this to verify your record', I trusted the form more.",
            "I'd usually bail when an app asks for my phone — the explanation made it feel reasonable.",
          ],
        },
      },
      {
        insight_id: "gi-progress-bar",
        title: "Progress indicator is the single highest-leverage UX element",
        type: "design",
        support_score: 0.88,
        confidence_bucket: "high",
        evidence: {
          summary:
            "Across qualitative and quantitative signals, the 'Step X of 4' indicator was the most frequently cited positive feature (mentioned in 28 of 42 sessions). Sessions where the bar was hidden in an A/A/B variant saw a 14% drop in completion rate.",
          quotes: [
            "The progress bar told me exactly how much was left. I never have that with paper forms.",
            "I almost stopped at Step 3, but the bar was 75% full and I figured I'd just finish.",
          ],
        },
      },
      {
        insight_id: "gi-conditional-medical",
        title:
          "Conditional medical questions outperform a static history block",
        type: "behavioral",
        support_score: 0.79,
        confidence_bucket: "medium",
        evidence: {
          summary:
            "Hiding medication, allergy, and chronic-condition fields behind toggles cut Step 3 abandonment by 64% without reducing the rate of disclosure for patients who do have relevant conditions (86% disclosed when prompted, vs. 81% in the legacy form).",
          quotes: [
            "I don't have allergies — being able to just say 'no' once and move on was a relief.",
            "When I said yes to medications, it asked me to add them one at a time, which was way easier than filling a long blank.",
          ],
        },
      },
      {
        insight_id: "gi-friction-residual",
        title: "Residual friction is concentrated at insurance Member ID",
        type: "operational",
        support_score: 0.66,
        confidence_bucket: "medium",
        evidence: {
          summary:
            "Of the 9% who did not complete the form, 4 of 4 cited Member ID entry as the blocker — they did not have their card on hand. This is the highest-ROI follow-up: a card-scan or 'I'll add it at the desk' option would likely close the remaining gap.",
          quotes: [
            "I gave up because I didn't have my insurance card and there was no way to skip.",
            "Let me upload a photo of the card next time — that would have saved me.",
          ],
        },
      },
    ],
    warnings: [],
  };

  const decisionSupportingResponses: ReportSupportingResponse[] = [
    {
      response_id: "dsr-1",
      title: "Front-desk lead, Riverside Medical Center",
      description:
        "We've been watching new-patient drop-off for two years. A 24-point lift in completion is not a UX nice-to-have — that's roughly 60 additional intakes per week at our volume.",
      question: null,
    },
    {
      response_id: "dsr-2",
      title: "Patient — first-time visit",
      description:
        "I've filled out a hundred of these forms in my life. This is the first one that didn't make me angry. I'd actually recommend this clinic just because of the check-in.",
      question: null,
    },
    {
      response_id: "dsr-3",
      title: "Patient — walk-in, mobile-only",
      description:
        "I did the whole thing on my phone in the waiting room. Walked in, sat down, was checked in before the receptionist looked up. That's the experience I want from healthcare.",
      question: null,
    },
    {
      response_id: "dsr-4",
      title: "Triage nurse",
      description:
        "The optional 'main concern' free-text field is the most useful single input we get. Don't remove it even though it's optional — keep it.",
      question: null,
    },
  ];

  const interviewQuestionsByRqId: Record<string, ReportInterviewQuestion[]> = {
    "rq-autofill": [
      {
        question_id: "iq-autofill-1",
        question:
          "How would you rate the effort required to complete the basic info step?",
        screen_label: "Screen 1 — Welcome & Basic Info",
        type: "likert",
        completion_rate: 1,
      },
      {
        question_id: "iq-autofill-2",
        question:
          "Did any of the pre-filled details surprise you, in a good or bad way?",
        screen_label: "Screen 1 — Welcome & Basic Info",
        type: "free-text",
        completion_rate: 0.91,
      },
      {
        question_id: "iq-autofill-3",
        question:
          "Were there any fields you expected to type yourself that the form filled for you?",
        screen_label: "Screen 1 — Welcome & Basic Info",
        type: "multiple-choice",
        completion_rate: 0.96,
      },
    ],
    "rq-step-vs-single": [
      {
        question_id: "iq-stepped-1",
        question:
          "Did the progress indicator help, hurt, or have no effect on your motivation to finish?",
        screen_label: "Screen 2 — Visit Information",
        type: "multiple-choice",
        completion_rate: 0.98,
      },
      {
        question_id: "iq-stepped-2",
        question:
          "Was there a step where you considered stopping? What was happening?",
        screen_label: "Screen 3 — Medical Overview",
        type: "free-text",
        completion_rate: 0.79,
      },
      {
        question_id: "iq-stepped-3",
        question:
          "Compared to a single-page form, did breaking this into steps feel faster, slower, or about the same?",
        screen_label: "Screen 4 — Insurance & Contact",
        type: "likert",
        completion_rate: 1,
      },
    ],
    "rq-mandatory-fields": [
      {
        question_id: "iq-mandatory-1",
        question:
          "Were there any required fields that felt unnecessary for a first visit?",
        screen_label: "Screen 1 — Welcome & Basic Info",
        type: "free-text",
        completion_rate: 0.83,
      },
      {
        question_id: "iq-mandatory-2",
        question:
          "When you saw the 'optional' label on the main concern box, did you fill it in anyway?",
        screen_label: "Screen 2 — Visit Information",
        type: "multiple-choice",
        completion_rate: 1,
      },
      {
        question_id: "iq-mandatory-3",
        question:
          "If a field were missing that the front desk later needed, how would you want to be asked for it?",
        screen_label: "Screen 4 — Insurance & Contact",
        type: "multiple-choice",
        completion_rate: 0.87,
      },
    ],
  };

  const participants: SyntheticParticipantRow[] = [
    {
      id: "P-01",
      name: "Maya R.",
      age: 29,
      visit_type: "New Patient",
      device: "Mobile",
      completion_time: "1m 42s",
      sentiment: "positive",
      quote:
        "It already knew my name and birthday — I just had to confirm. That felt really respectful of my time.",
    },
    {
      id: "P-02",
      name: "Aisha M.",
      age: 34,
      visit_type: "Walk-in",
      device: "Mobile",
      completion_time: "2m 11s",
      sentiment: "positive",
      quote:
        "Seeing 'Step 2 of 4' made it feel finite. The old form felt like it would never end.",
    },
    {
      id: "P-03",
      name: "James W.",
      age: 47,
      visit_type: "New Patient",
      device: "Desktop",
      completion_time: "3m 04s",
      sentiment: "positive",
      quote:
        "I'm not great with phones, so I did it on my laptop in the car. The same form worked fine on desktop, which surprised me.",
    },
    {
      id: "P-04",
      name: "Priya S.",
      age: 26,
      visit_type: "Walk-in",
      device: "Mobile",
      completion_time: "1m 58s",
      sentiment: "positive",
      quote: "Less typing on a small screen is huge. My thumbs thank you.",
    },
    {
      id: "P-05",
      name: "Jordan L.",
      age: 31,
      visit_type: "New Patient",
      device: "Mobile",
      completion_time: "2m 22s",
      sentiment: "positive",
      quote:
        "I was relieved I didn't have to dig out my insurance card before I'd even sat down.",
    },
    {
      id: "P-06",
      name: "Elena V.",
      age: 58,
      visit_type: "Follow-up",
      device: "Mobile",
      completion_time: "2m 47s",
      sentiment: "positive",
      quote:
        "The buttons are big enough that my reading glasses aren't even on yet and I can still read everything. That matters.",
    },
    {
      id: "P-07",
      name: "Marcus T.",
      age: 22,
      visit_type: "Walk-in",
      device: "Mobile",
      completion_time: "1m 19s",
      sentiment: "positive",
      quote:
        "Honestly the fastest check-in I've done. Faster than ordering food.",
    },
    {
      id: "P-08",
      name: "Daniel K.",
      age: 41,
      visit_type: "New Patient",
      device: "Mobile",
      completion_time: "2m 08s",
      sentiment: "neutral",
      quote:
        "I almost missed the auto-filled phone number was wrong — would be nice if it highlighted what was pre-filled.",
    },
    {
      id: "P-09",
      name: "Sofia G.",
      age: 35,
      visit_type: "New Patient",
      device: "Mobile",
      completion_time: "2m 31s",
      sentiment: "positive",
      quote:
        "I liked that the medical questions only opened up if I said yes. Felt like the form was paying attention.",
    },
    {
      id: "P-10",
      name: "Tom B.",
      age: 52,
      visit_type: "Follow-up",
      device: "Desktop",
      completion_time: "2m 49s",
      sentiment: "positive",
      quote: "Each screen had one job. I never wondered what to do next.",
    },
    {
      id: "P-11",
      name: "Riya N.",
      age: 28,
      visit_type: "New Patient",
      device: "Mobile",
      completion_time: "1m 51s",
      sentiment: "positive",
      quote:
        "When the little (i) explained why they need my date of birth, I trusted the form more.",
    },
    {
      id: "P-12",
      name: "Chris D.",
      age: 44,
      visit_type: "Walk-in",
      device: "Mobile",
      completion_time: "2m 03s",
      sentiment: "positive",
      quote:
        "I filled this out in line at the pharmacy on my phone. That would not have been possible with the PDF.",
    },
    {
      id: "P-13",
      name: "Lena O.",
      age: 38,
      visit_type: "New Patient",
      device: "Mobile",
      completion_time: "2m 37s",
      sentiment: "positive",
      quote:
        "The progress bar gave me confidence I was almost done. I would have given up on a wall of fields.",
    },
    {
      id: "P-14",
      name: "Sam P.",
      age: 33,
      visit_type: "Walk-in",
      device: "Mobile",
      completion_time: "1m 47s",
      sentiment: "positive",
      quote:
        "Honestly the old form had me convinced I was about to be billed for something. This one felt like a normal app.",
    },
    {
      id: "P-15",
      name: "Hannah F.",
      age: 24,
      visit_type: "New Patient",
      device: "Mobile",
      completion_time: "1m 33s",
      sentiment: "positive",
      quote:
        "Auto-fill from my saved profile meant I literally just tapped Continue, Continue, Continue. Took maybe 90 seconds.",
    },
    {
      id: "P-16",
      name: "Andre B.",
      age: 49,
      visit_type: "Follow-up",
      device: "Desktop",
      completion_time: "3m 21s",
      sentiment: "neutral",
      quote:
        "I'd actually rather have one long page so I can scan ahead. The steps are nice for new people but slowed me down.",
    },
    {
      id: "P-17",
      name: "Carla T.",
      age: 36,
      visit_type: "New Patient",
      device: "Mobile",
      completion_time: "2m 18s",
      sentiment: "positive",
      quote:
        "I appreciated that 'reason for visit' was free text — I could just say 'sore throat for 4 days' instead of guessing the right category.",
    },
    {
      id: "P-18",
      name: "Yuki H.",
      age: 27,
      visit_type: "Walk-in",
      device: "Mobile",
      completion_time: "2m 02s",
      sentiment: "positive",
      quote:
        "English isn't my first language and the simple wording really helped. No medical jargon.",
    },
    {
      id: "P-19",
      name: "Robert M.",
      age: 65,
      visit_type: "Follow-up",
      device: "Desktop",
      completion_time: "3m 56s",
      sentiment: "positive",
      quote:
        "Took me a moment to find where to type, but once I did, it was very straightforward. Better than anything I've seen at a doctor's office.",
    },
    {
      id: "P-20",
      name: "Bianca P.",
      age: 30,
      visit_type: "New Patient",
      device: "Mobile",
      completion_time: "2m 14s",
      sentiment: "positive",
      quote:
        "I have allergies and toggling that on and adding 'penicillin' was so much easier than a blank text field.",
    },
    {
      id: "P-21",
      name: "Kwame O.",
      age: 39,
      visit_type: "Walk-in",
      device: "Mobile",
      completion_time: "1m 49s",
      sentiment: "positive",
      quote:
        "I did the whole thing on my phone in the waiting room. Walked in, sat down, was checked in before the receptionist looked up.",
    },
    {
      id: "P-22",
      name: "Megan R.",
      age: 42,
      visit_type: "New Patient",
      device: "Desktop",
      completion_time: "2m 56s",
      sentiment: "positive",
      quote:
        "The 'Why we need this' tooltip on phone number was the moment I decided to keep going. I usually bail there.",
    },
    {
      id: "P-23",
      name: "Lucas A.",
      age: 19,
      visit_type: "Walk-in",
      device: "Mobile",
      completion_time: "1m 28s",
      sentiment: "positive",
      quote:
        "Big tap targets, no zooming in, no fighting the keyboard. It just worked.",
    },
    {
      id: "P-24",
      name: "Nadia E.",
      age: 33,
      visit_type: "New Patient",
      device: "Mobile",
      completion_time: "2m 41s",
      sentiment: "positive",
      quote:
        "I added two medications and could remove one when I noticed I'd duplicated it — that's never been possible on paper.",
    },
    {
      id: "P-25",
      name: "Patrick S.",
      age: 56,
      visit_type: "Follow-up",
      device: "Desktop",
      completion_time: "3m 12s",
      sentiment: "positive",
      quote:
        "I was expecting the usual eight pages of legal text. The fact that there was almost nothing optional made me trust the clinic more, not less.",
    },
    {
      id: "P-26",
      name: "Theresa W.",
      age: 60,
      visit_type: "Follow-up",
      device: "Mobile",
      completion_time: "3m 02s",
      sentiment: "positive",
      quote:
        "I appreciated that I could pause and come back to it. The 'Draft saved' note made me relax.",
    },
    {
      id: "P-27",
      name: "Dev I.",
      age: 25,
      visit_type: "New Patient",
      device: "Mobile",
      completion_time: "1m 56s",
      sentiment: "positive",
      quote:
        "First medical form I've filled out where I didn't feel like I needed a lawyer. Good copy.",
    },
    {
      id: "P-28",
      name: "Olivia J.",
      age: 32,
      visit_type: "Walk-in",
      device: "Mobile",
      completion_time: "2m 12s",
      sentiment: "positive",
      quote:
        "Loved that I didn't have to type my address — picking from the dropdown was instant.",
    },
    {
      id: "P-29",
      name: "Ahmed Q.",
      age: 46,
      visit_type: "New Patient",
      device: "Mobile",
      completion_time: "2m 33s",
      sentiment: "positive",
      quote:
        "I usually screenshot forms because I'm worried I'll lose my place. With the steps and the auto-save, I didn't feel I needed to.",
    },
    {
      id: "P-30",
      name: "Grace L.",
      age: 21,
      visit_type: "Walk-in",
      device: "Mobile",
      completion_time: "1m 31s",
      sentiment: "positive",
      quote:
        "Honestly the cleanest form I've ever filled out. It's the only check-in I've finished without sighing.",
    },
    {
      id: "P-31",
      name: "Brendan H.",
      age: 51,
      visit_type: "Follow-up",
      device: "Desktop",
      completion_time: "2m 58s",
      sentiment: "positive",
      quote:
        "On desktop it still felt mobile-first, which I mean as a compliment. No clutter.",
    },
    {
      id: "P-32",
      name: "Anika V.",
      age: 37,
      visit_type: "New Patient",
      device: "Mobile",
      completion_time: "2m 26s",
      sentiment: "positive",
      quote:
        "The 'main concern' free-text was the best part — I could explain in my own words instead of choosing a wrong category.",
    },
    {
      id: "P-33",
      name: "Eli S.",
      age: 28,
      visit_type: "Walk-in",
      device: "Mobile",
      completion_time: "1m 44s",
      sentiment: "positive",
      quote:
        "Asking 'is this you?' on the auto-filled name would be even better — but small thing. The form is great.",
    },
    {
      id: "P-34",
      name: "Mira C.",
      age: 40,
      visit_type: "New Patient",
      device: "Mobile",
      completion_time: "2m 19s",
      sentiment: "positive",
      quote:
        "I have two kids and I filled this out one-handed while holding the younger one. That's never happened before.",
    },
    {
      id: "P-35",
      name: "Felix N.",
      age: 23,
      visit_type: "Walk-in",
      device: "Mobile",
      completion_time: "1m 37s",
      sentiment: "positive",
      quote: "Done in under two minutes. Wish all forms were like this.",
    },
    {
      id: "P-36",
      name: "Zara H.",
      age: 31,
      visit_type: "New Patient",
      device: "Mobile",
      completion_time: "2m 47s",
      sentiment: "neutral",
      quote:
        "I gave up at the insurance Member ID step because I didn't have my card on hand. Came back later and finished — but a 'I'll add it at the desk' option would have helped.",
    },
    {
      id: "P-37",
      name: "Ravi K.",
      age: 45,
      visit_type: "Follow-up",
      device: "Mobile",
      completion_time: "2m 51s",
      sentiment: "positive",
      quote:
        "As a returning patient most of my info was prefilled. I just confirmed and moved on.",
    },
    {
      id: "P-38",
      name: "Sienna B.",
      age: 26,
      visit_type: "New Patient",
      device: "Mobile",
      completion_time: "1m 53s",
      sentiment: "positive",
      quote:
        "The form felt like a friendly intake conversation, not a legal document. That's a huge upgrade.",
    },
    {
      id: "P-39",
      name: "Tariq Y.",
      age: 53,
      visit_type: "New Patient",
      device: "Desktop",
      completion_time: "3m 08s",
      sentiment: "positive",
      quote:
        "The form respected that I might not know my exact medication dosages. Letting me skip it without nagging was the right call.",
    },
    {
      id: "P-40",
      name: "Hailey U.",
      age: 29,
      visit_type: "Walk-in",
      device: "Mobile",
      completion_time: "2m 06s",
      sentiment: "positive",
      quote:
        "I'd actually recommend this clinic just because of the check-in. Sounds dramatic but it sets the tone.",
    },
    {
      id: "P-41",
      name: "Marco G.",
      age: 67,
      visit_type: "Follow-up",
      device: "Desktop",
      completion_time: "4m 02s",
      sentiment: "positive",
      quote:
        "Slow on my end but I never felt confused. The big text and the one-thing-per-screen layout work for older patients too.",
    },
    {
      id: "P-42",
      name: "Layla F.",
      age: 30,
      visit_type: "New Patient",
      device: "Mobile",
      completion_time: "2m 13s",
      sentiment: "positive",
      quote:
        "I almost stopped at Step 3, but the bar was 75% full and I figured I'd just finish.",
    },
  ];

  return {
    insightsData,
    metricsOverride: {
      completion_rate: 91,
      average_time_to_complete: "2m 34s",
    },
    decisionSupportingResponses,
    interviewQuestionsByRqId,
    participants,
  };
}

function buildCreditCardStreamliningOverride(): SyntheticInsightsOverride {
  const timestamp = new Date().toISOString();

  const insightsData: ExperimentInsightsData = {
    id: "synthetic-credit-card-streamlining",
    experiment_id: "synthetic-credit-card-streamlining",
    analysis_run_id: "synthetic-run-1",
    status: "generated",
    timestamp,
    analysis_summary: {
      participant_count: 44,
      question_coverage: 1,
      free_text_response_rate: 0.86,
      eligible_rq_count: 3,
      insufficient_evidence_rq_count: 0,
    },
    final_decision: {
      decision_label: "pivot",
      pursue_probability: 0.18,
      pivot_probability: 0.71,
      pause_probability: 0.11,
      confidence_bucket: "high",
      impact_score: 7.4,
      verbalized_decision:
        "The streamlined 5-step flow more than doubled application completion (34% → 62%) and met the under-5-minute target (median 4m 11s) — both wins on the headline metrics. But the same cohort activated their card at 53% within 14 days, vs. 81% for the legacy 12-step control. Speed without trust signals produced applicants, not cardholders. Recommend pivoting to a prequalify-first flow that preserves the cognitive-load gains but rebuilds explicit confidence — particularly around the auto-populated income step, which drove the largest single trust drop in the experiment.",
      rationale:
        "Two of three core research questions returned negative or qualified results. RQ1 (auto-populated income trust): 32 of 44 participants flagged the silent auto-fill as 'unsettling' or 'too easy to be real,' producing a -1.8-point drop on the 7-point Likert vs. the manual-entry control. RQ2 (cognitive load): cleanly supported — the form IS easier to fill out. RQ3 (under-5-minute completion): technically met but masked by the activation collapse. The pivot direction is therefore not 'undo simplification' but 'preserve simplification, restore confidence' — a meaningful refinement of the original hypothesis rather than abandonment of it.",
      recommended_actions: [
        "Pivot to a prequalify-first flow: surface the personalized offer (Screen 3) before requesting identity data on Screen 2. Use only zip code + DOB + last-4 SSN for the soft pull. Preserves speed while front-loading the value signal.",
        "Replace silent auto-population with explicit, attributed verification. Show 'Chase verified your $98,400 income via your Premier Checking account on Apr 21' with a clear 'this isn't right' override. Trust requires attribution, not just accuracy.",
        "Reintroduce a 7-second 'review your application' step before submit. Removing it shaved 40 seconds but cost 28 percentage points of activation — a poor trade.",
        "Re-segment the experiment for existing-Chase-customers vs. net-new-applicants. Existing customers completed at 78%; new applicants at 51%. The trust pivot likely matters more for the latter.",
        "Run a 6-week follow-on test isolating the prequalify-first variant against the legacy control before any production rollout. The pivot should be validated, not assumed.",
      ],
    },
    core_research_question_results: [
      {
        rq_id: "rq-cc-trust-autofill",
        research_question:
          "Will users trust the new auto-populated income verification from connected bank accounts?",
        answer_label: "not_supported",
        positive_posterior: 0.11,
        negative_posterior: 0.84,
        certainty: 0.84,
        evidence_quality: 0.89,
        confidence_score: 0.86,
        confidence_bucket: "high",
        status: "answered",
        verbalized_answer:
          "No — silent auto-population from connected Chase accounts measurably reduced trust at the most consequential decision point. 32 of 44 participants flagged the auto-filled income as 'too easy' or 'unsettling.' Median trust score on the verification step dropped from 6.1 (manual-entry control) to 4.3 (test arm). Notably, the income amount being correct didn't matter — participants needed visible attribution ('how did you verify this?') to convert that accuracy into confidence.",
        rationale:
          "The verification step was the highest-friction moment in the test arm despite being technically the lowest-effort. 8 of the 17 abandons happened on Screen 2 (Identity Verification), and every one of those participants cited the auto-fill specifically when asked. In one case, the auto-filled income was actually wrong (last year's data) which produced a stronger negative reaction than missing data would have — accuracy hides behind attribution.",
        observed_factors: [
          {
            factor_id: "customer-tenure",
            factor_name: "Customer Tenure",
            top_value: "Net-new applicant",
            top_value_posterior: 0.71,
          },
          {
            factor_id: "income-source-visible",
            factor_name: "Attribution Visible",
            top_value: "No",
            top_value_posterior: 0.93,
          },
        ],
        risk_flags: [
          {
            flag: "Regulatory exposure",
            severity: "medium",
            detail:
              "If a participant disputes an auto-populated income figure post-funding, the lack of an explicit attestation step may complicate Reg Z compliance. Legal review recommended before any rollout.",
          },
          {
            flag: "Reputation risk",
            severity: "low",
            detail:
              "Two participants in interviews used the word 'creepy' to describe the auto-fill experience. Word-of-mouth risk is non-zero in the existing-cardholder segment.",
          },
        ],
        evidence_trace: [
          {
            participant_id: "CC-28",
            participant_name: "Brian C.",
            exemplar_quotes: [
              "When my income just appeared, I bailed. I have no idea where Chase pulled that from. Felt creepy.",
              "I would have happily typed my income. The two minutes saved was not worth the unease.",
            ],
          },
          {
            participant_id: "CC-29",
            participant_name: "Cassandra O.",
            exemplar_quotes: [
              "The income wasn't even right — it showed last year's, not this year's. I gave up.",
            ],
          },
          {
            participant_id: "CC-33",
            participant_name: "Tessa F.",
            exemplar_quotes: [
              "It assumed I made $94k. I make $52k. I worried I was about to take a credit line I couldn't pay.",
            ],
          },
          {
            participant_id: "CC-30",
            participant_name: "Doug R.",
            exemplar_quotes: [
              "Auto-population without explanation is a non-starter for someone in my generation. Tell me what you're using.",
            ],
          },
        ],
      },
      {
        rq_id: "rq-cc-cognitive-load",
        research_question:
          "Does the simplified flow reduce cognitive load and time-to-completion as expected?",
        answer_label: "supported",
        positive_posterior: 0.92,
        negative_posterior: 0.04,
        certainty: 0.92,
        evidence_quality: 0.87,
        confidence_score: 0.9,
        confidence_bucket: "high",
        status: "answered",
        verbalized_answer:
          "Yes — the simplified flow cleanly reduced cognitive load. Self-reported effort dropped from 5.4 (legacy) to 2.9 (test) on the NASA-TLX-derived 7-point scale. 38 of 44 participants used the word 'easier,' 'simpler,' or 'painless' unprompted in post-task interviews. Median completion time dropped from 22m 47s to 4m 11s — an 82% reduction. This part of the hypothesis held.",
        rationale:
          "Reducing the form from 12 steps to 5 with conditional fields and inline validation is doing exactly what hypothesized. Eye-tracking and tap-events show no regions of confusion or backtracking on Screens 1, 4, or 5. The win is real and should be preserved through any pivot.",
        observed_factors: [
          {
            factor_id: "device",
            factor_name: "Device",
            top_value: "Mobile",
            top_value_posterior: 0.67,
          },
          {
            factor_id: "screen-1-engagement",
            factor_name: "Screen 1 dwell time",
            top_value: "18-25s",
            top_value_posterior: 0.59,
          },
        ],
        risk_flags: [],
        evidence_trace: [
          {
            participant_id: "CC-05",
            participant_name: "Marcus D.",
            exemplar_quotes: [
              "I'm 51 and I usually struggle with these forms. This one I finished one-handed on the train.",
            ],
          },
          {
            participant_id: "CC-09",
            participant_name: "Dario C.",
            exemplar_quotes: [
              "Done in 3 minutes from start to Wallet. I've never said this about a financial product before.",
            ],
          },
          {
            participant_id: "CC-14",
            participant_name: "Rosa V.",
            exemplar_quotes: [
              "I am a long-time Chase customer and this is the cleanest application I have ever filled out. Bravo to whoever designed it.",
            ],
          },
          {
            participant_id: "CC-04",
            participant_name: "Emily W.",
            exemplar_quotes: [
              "Loved Screen 1 — picking 'Earn Rewards' actually changed what I saw next. That's smart.",
            ],
          },
        ],
      },
      {
        rq_id: "rq-cc-time-target",
        research_question:
          "Can users complete the new streamlined flow in under 5 minutes with fewer errors?",
        answer_label: "supported",
        positive_posterior: 0.79,
        negative_posterior: 0.16,
        certainty: 0.79,
        evidence_quality: 0.74,
        confidence_score: 0.77,
        confidence_bucket: "medium",
        status: "answered",
        verbalized_answer:
          "Yes, but with a critical caveat. Median completion time was 4m 11s, and 91% of completers finished under the 5-minute target. Inline-validation error rate dropped from 11.4% (legacy) to 4.2% (test). However, the same cohort activated their card at 53% within 14 days, vs. 81% for the legacy 12-step arm. The under-5-minute speed correlated negatively with post-application commitment. Hitting the speed target alone was not sufficient — and may have been counterproductive — without paired trust-building.",
        rationale:
          "The 5-minute target was a proxy metric for 'reduced friction.' The proxy was achieved. But we picked the wrong proxy: friction is not what was driving the original 40% drop-off. The real driver was uncertainty about offer credibility, which only manifests post-application as activation hesitancy. The headline number is met; the underlying assumption is wrong.",
        observed_factors: [
          {
            factor_id: "completion-time-bucket",
            factor_name: "Completion Time Bucket",
            top_value: "<3 min",
            top_value_posterior: 0.42,
          },
          {
            factor_id: "activation-rate",
            factor_name: "14-day Activation Rate",
            top_value: "53%",
            top_value_posterior: 1,
          },
        ],
        risk_flags: [
          {
            flag: "Wrong proxy metric",
            severity: "high",
            detail:
              "Speed-of-completion does not predict cardholder commitment. Future tests should use 14-day activation as the primary success metric, not flow completion.",
          },
        ],
        evidence_trace: [
          {
            participant_id: "CC-25",
            participant_name: "Drew H.",
            exemplar_quotes: [
              "I haven't activated. I think I might forget about it, honestly. The flow was almost too easy to remember.",
            ],
          },
          {
            participant_id: "CC-27",
            participant_name: "Yvette S.",
            exemplar_quotes: [
              "I finished and was approved but I felt rushed into approval. I'd actually rather a slower flow that lets me feel like I'm committing.",
            ],
          },
          {
            participant_id: "CC-22",
            participant_name: "Sasha V.",
            exemplar_quotes: [
              "I think I'm going to wait. The whole thing felt like it deserved more friction at the end. Like a 'are you sure?' moment.",
            ],
          },
          {
            participant_id: "CC-37",
            participant_name: "Helena K.",
            exemplar_quotes: [
              "$45k offer in 90 seconds. That's not a serious offer. I'd rather Chase took 5 more minutes to make me feel it was earned.",
            ],
          },
        ],
      },
    ],
    general_insights: [
      {
        insight_id: "gi-cc-fast-low-stakes",
        title:
          "The fast-equals-low-stakes effect: speed correlated negatively with activation",
        type: "behavioral",
        support_score: 0.91,
        confidence_bucket: "high",
        evidence: {
          summary:
            "Across the test cohort, every additional 60 seconds spent in the application correlated with a 4.2-point lift in 14-day activation rate. Participants who finished in under 3 minutes activated at 38%. Participants who finished in 4-5 minutes activated at 61%. Speed past a threshold appears to signal 'this isn't a serious financial product' rather than 'this respects my time.' This is the single most important finding of the experiment and the primary driver of the pivot recommendation.",
          quotes: [
            "Honestly the speed scared me. I kept thinking, 'wait, did I just take on $24,000 of credit in three minutes?'",
            "I prefer when financial decisions feel like decisions, not like ordering takeout.",
          ],
        },
      },
      {
        insight_id: "gi-cc-attribution-not-accuracy",
        title:
          "Auto-populated income failed on attribution, not accuracy — fix is small in scope",
        type: "qualitative",
        support_score: 0.88,
        confidence_bucket: "high",
        evidence: {
          summary:
            "92% of explicit trust complaints in interviews and free-text feedback referenced one specific moment: the income field arriving pre-filled. Participants did not object to Chase using their data — they objected to NOT being told how it was used. Adding a single line of attribution ('Chase verified this from your Premier Checking on [date], looks wrong?') is likely sufficient. The redesign does not require structural change. This is a high-leverage, low-cost fix.",
          quotes: [
            "If it had said 'we got this from your direct deposits' I would have been fine. Just tell me.",
            "I don't mind Chase knowing my income. I mind Chase pretending it's something else.",
          ],
        },
      },
      {
        insight_id: "gi-cc-segment-differential",
        title:
          "Existing Chase customers vs. new applicants behave like two different audiences",
        type: "segment",
        support_score: 0.84,
        confidence_bucket: "high",
        evidence: {
          summary:
            "Existing Chase customers completed at 78%; net-new applicants at 51%. The activation gap was even larger: 71% vs. 32%. Existing-customer trust transferred to the new flow; net-new-applicant trust did not. The simplified flow disproportionately benefits the audience that already trusts Chase. Any rollout strategy must consider segment-targeted variants — what works for an existing Sapphire holder will not work for a 26-year-old applying for their first card.",
          quotes: [
            "Already had Freedom Unlimited so most of my info was already in your system. Felt seamless.",
            "First credit card with Chase. The 'Why this offer?' explainer was the only thing that convinced me.",
          ],
        },
      },
      {
        insight_id: "gi-cc-mobile-first",
        title:
          "Mobile-first paid off — 67% of completed applications were on mobile",
        type: "design",
        support_score: 0.78,
        confidence_bucket: "high",
        evidence: {
          summary:
            "67% of applications were submitted on mobile. Mobile completion rate (66%) exceeded desktop (54%). The Apple Wallet add was the single most-praised feature in mobile sessions. The mobile-first hypothesis was correct and should be preserved through any pivot. This validates one of the three key assumptions and should not be re-litigated.",
          quotes: [
            "Three taps and I had a card. The Wallet integration was instant.",
            "On my iPhone, the whole thing was painless. Really impressive.",
          ],
        },
      },
      {
        insight_id: "gi-cc-why-this-offer",
        title:
          "Users wanted MORE context, not less — 'Why this offer?' was the most-engaged element",
        type: "behavioral",
        support_score: 0.81,
        confidence_bucket: "high",
        evidence: {
          summary:
            "When given the option, 78% of completers expanded the 'Why this offer?' explainer on Screen 3. Median dwell time on the expanded view was 47 seconds — long enough to fundamentally change the speed narrative. Participants are not actually asking for less information; they're asking for less REQUIRED information presented at higher density and context. This insight should anchor the pivot — keep the form short, but make optional context rich and obvious.",
          quotes: [
            "The 'Why this offer?' explainer convinced me. Without it I would have been suspicious of the limit.",
            "I expanded it because I wanted to know what Chase saw in my profile. That answer mattered more than the speed.",
          ],
        },
      },
    ],
    warnings: [
      "Activation rate is a 14-day proxy; longer-window data may shift the magnitude of the effect.",
    ],
  };

  const decisionSupportingResponses: ReportSupportingResponse[] = [
    {
      response_id: "cc-dsr-1",
      title: "VP, Card Acquisition",
      description:
        "A 28-point activation gap is not a UX detail — that's a P&L issue. Two-thirds of revenue from this product comes after activation. The completion-rate win is meaningless if we cut activation by a third. The pivot recommendation is the right call.",
      question: null,
    },
    {
      response_id: "cc-dsr-2",
      title: "Director, Compliance & Privacy",
      description:
        "From a Reg Z and consumer-disclosure standpoint, silent auto-population is a real exposure. We need attestation language at the point of use. The recommended attribution fix solves the legal concern and the trust concern at the same time — high-leverage.",
      question: null,
    },
    {
      response_id: "cc-dsr-3",
      title: "Lead Designer, Premium Cards",
      description:
        "The cognitive-load gains are real and we should not throw them away. The pivot is genuinely a refinement of the original hypothesis, not a reversal. Keep the 5-step structure; add explicit attribution and a confidence-building review step.",
      question: null,
    },
    {
      response_id: "cc-dsr-4",
      title: "Existing Chase customer (Sapphire holder, 9 yrs)",
      description:
        "I'd actually rather Chase took an extra minute and made the application feel like a financial decision. The speed felt like ordering food. That's not what I want when I'm taking on credit.",
      question: null,
    },
  ];

  const interviewQuestionsByRqId: Record<string, ReportInterviewQuestion[]> = {
    "rq-cc-trust-autofill": [
      {
        question_id: "iq-cc-trust-1",
        question:
          "When the form auto-filled your income from your Chase account, did that feel reassuring or unsettling?",
        screen_label: "Screen 2 — Identity Verification",
        type: "likert",
        completion_rate: 0.95,
      },
      {
        question_id: "iq-cc-trust-2",
        question:
          "Would you have preferred to enter your income manually, even if it took longer?",
        screen_label: "Screen 2 — Identity Verification",
        type: "multiple-choice",
        completion_rate: 1,
      },
      {
        question_id: "iq-cc-trust-3",
        question:
          "How confident were you that the income shown was accurate and current?",
        screen_label: "Screen 2 — Identity Verification",
        type: "likert",
        completion_rate: 0.91,
      },
      {
        question_id: "iq-cc-trust-4",
        question:
          "Did you feel Chase had clearly communicated where the auto-filled data came from?",
        screen_label: "Screen 2 — Identity Verification",
        type: "free-text",
        completion_rate: 0.84,
      },
    ],
    "rq-cc-cognitive-load": [
      {
        question_id: "iq-cc-load-1",
        question:
          "Compared to other credit-card applications you've completed, how mentally taxing was this one?",
        screen_label: "Screen 1 — Goal Selection",
        type: "likert",
        completion_rate: 1,
      },
      {
        question_id: "iq-cc-load-2",
        question:
          "On Screen 1 (Goal Selection), did the goal cards help you focus or get in your way?",
        screen_label: "Screen 1 — Goal Selection",
        type: "multiple-choice",
        completion_rate: 0.98,
      },
      {
        question_id: "iq-cc-load-3",
        question:
          "Were the collapsible Terms & Conditions sections clear, or did they feel like they were hiding something?",
        screen_label: "Screen 4 — Terms & Conditions",
        type: "free-text",
        completion_rate: 0.81,
      },
      {
        question_id: "iq-cc-load-4",
        question:
          "Did you feel like the form asked you to make any decisions you weren't ready to make?",
        screen_label: "Screen 4 — Terms & Conditions",
        type: "multiple-choice",
        completion_rate: 0.86,
      },
    ],
    "rq-cc-time-target": [
      {
        question_id: "iq-cc-time-1",
        question: "Did this application feel rushed, well-paced, or slow?",
        screen_label: "Screen 5 — Virtual Card Ready",
        type: "multiple-choice",
        completion_rate: 1,
      },
      {
        question_id: "iq-cc-time-2",
        question:
          "When you saw the personalized offer on Screen 3, did the speed of getting there make you trust the offer more or less?",
        screen_label: "Screen 3 — Personalized Offer Preview",
        type: "likert",
        completion_rate: 0.96,
      },
      {
        question_id: "iq-cc-time-3",
        question:
          "On Screen 5 (Virtual Card Ready), did getting a card that quickly feel exciting or suspicious?",
        screen_label: "Screen 5 — Virtual Card Ready",
        type: "free-text",
        completion_rate: 0.78,
      },
      {
        question_id: "iq-cc-time-4",
        question:
          "If you finished the application but didn't activate the card, what was the reason?",
        screen_label: "Screen 5 — Virtual Card Ready",
        type: "free-text",
        completion_rate: 0.66,
      },
    ],
  };

  const participants: SyntheticParticipantRow[] = [
    {
      id: "CC-01",
      name: "David Lin",
      age: 38,
      visit_type: "Existing Customer",
      device: "Mobile",
      completion_time: "Activated",
      sentiment: "positive",
      quote:
        "Sapphire holder for 3 years. The flow took me about 4 minutes, used Wallet to add, easy.",
    },
    {
      id: "CC-02",
      name: "Carla Reyes",
      age: 44,
      visit_type: "Premier Customer",
      device: "Mobile",
      completion_time: "Activated",
      sentiment: "positive",
      quote: "Three taps and I had a card. The Wallet integration was instant.",
    },
    {
      id: "CC-03",
      name: "Jamal Ferguson",
      age: 31,
      visit_type: "Existing Customer",
      device: "Mobile",
      completion_time: "Activated",
      sentiment: "positive",
      quote:
        "Already had Freedom Unlimited so most of my info was already in your system. Felt seamless.",
    },
    {
      id: "CC-04",
      name: "Emily Walsh",
      age: 42,
      visit_type: "Premier Customer",
      device: "Desktop",
      completion_time: "Activated",
      sentiment: "positive",
      quote:
        "Loved Screen 1 — picking 'Earn Rewards' actually changed what I saw next. That's smart.",
    },
    {
      id: "CC-05",
      name: "Marcus Doyle",
      age: 51,
      visit_type: "Existing Customer",
      device: "Mobile",
      completion_time: "Activated",
      sentiment: "positive",
      quote:
        "I'm 51 and I usually struggle with these forms. This one I finished one-handed on the train.",
    },
    {
      id: "CC-06",
      name: "Naomi Park",
      age: 29,
      visit_type: "New Applicant",
      device: "Mobile",
      completion_time: "Activated",
      sentiment: "positive",
      quote:
        "First credit card with Chase. The 'Why this offer?' explainer convinced me. Activated same day.",
    },
    {
      id: "CC-07",
      name: "Greg Sanchez",
      age: 39,
      visit_type: "Existing Customer",
      device: "Mobile",
      completion_time: "Activated",
      sentiment: "positive",
      quote:
        "The animated card reveal was a little cheesy but I'll admit it worked.",
    },
    {
      id: "CC-08",
      name: "Pamela O'Connor",
      age: 56,
      visit_type: "Premier Customer",
      device: "Desktop",
      completion_time: "Activated",
      sentiment: "neutral",
      quote:
        "Approved at $32k limit. I'd asked for $25k. The system being too generous made me trust the offer LESS, not more.",
    },
    {
      id: "CC-09",
      name: "Dario Costa",
      age: 35,
      visit_type: "Existing Customer",
      device: "Mobile",
      completion_time: "Activated",
      sentiment: "positive",
      quote:
        "Done in 3 minutes from start to Wallet. I've never said this about a financial product before.",
    },
    {
      id: "CC-10",
      name: "Alyssa Bridge",
      age: 27,
      visit_type: "New Applicant",
      device: "Mobile",
      completion_time: "Activated",
      sentiment: "positive",
      quote:
        "Credit-builder card and I was nervous. The form made it feel less intimidating.",
    },
    {
      id: "CC-11",
      name: "Benjamin Hu",
      age: 47,
      visit_type: "Premier Customer",
      device: "Desktop",
      completion_time: "Activated",
      sentiment: "positive",
      quote:
        "I run a small business. Time matters. Four minutes for a personal card was respectful.",
    },
    {
      id: "CC-12",
      name: "Sondra Lee",
      age: 33,
      visit_type: "Existing Customer",
      device: "Mobile",
      completion_time: "Activated",
      sentiment: "positive",
      quote:
        "Already had checking with you so the offer felt earned, not just thrown at me.",
    },
    {
      id: "CC-13",
      name: "Theo Marshall",
      age: 40,
      visit_type: "Existing Customer",
      device: "Mobile",
      completion_time: "Activated",
      sentiment: "positive",
      quote:
        "Form was great. Activation took me a few days because I wasn't sure I needed it. Maybe ask me later why I applied.",
    },
    {
      id: "CC-14",
      name: "Rosa Velasquez",
      age: 58,
      visit_type: "Premier Customer",
      device: "Desktop",
      completion_time: "Activated",
      sentiment: "positive",
      quote:
        "I am a long-time Chase customer and this is the cleanest application I have ever filled out. Bravo to whoever designed it.",
    },
    {
      id: "CC-15",
      name: "Tyler Brennan",
      age: 32,
      visit_type: "New Applicant",
      device: "Mobile",
      completion_time: "Approved (No Activation)",
      sentiment: "neutral",
      quote:
        "Got approved fast but I'm still sitting on the email. Didn't feel like I committed to anything yet.",
    },
    {
      id: "CC-16",
      name: "Janet Soto",
      age: 45,
      visit_type: "Existing Customer",
      device: "Mobile",
      completion_time: "Approved (No Activation)",
      sentiment: "neutral",
      quote:
        "Form was easy, but I want to read the rate disclosures more carefully before I activate. The summary was a little too cheerful.",
    },
    {
      id: "CC-17",
      name: "Khalid Rahman",
      age: 36,
      visit_type: "New Applicant",
      device: "Desktop",
      completion_time: "Approved (No Activation)",
      sentiment: "positive",
      quote:
        "Filled it out, approved for $18k, but I'm not in a rush. I want to compare offers.",
    },
    {
      id: "CC-18",
      name: "Linda McKee",
      age: 49,
      visit_type: "Existing Customer",
      device: "Mobile",
      completion_time: "Approved (No Activation)",
      sentiment: "neutral",
      quote:
        "Activation isn't quite the same energy as approval. I'll get to it this weekend.",
    },
    {
      id: "CC-19",
      name: "Andre Wexler",
      age: 41,
      visit_type: "Existing Customer",
      device: "Mobile",
      completion_time: "Approved (No Activation)",
      sentiment: "neutral",
      quote:
        "I think I just wanted to see what offer I'd get. The form being short made it easy to apply 'just to check.'",
    },
    {
      id: "CC-20",
      name: "Dianne Kowalski",
      age: 53,
      visit_type: "Premier Customer",
      device: "Desktop",
      completion_time: "Approved (No Activation)",
      sentiment: "neutral",
      quote:
        "I appreciated the speed but I prefer to call my banker before activating new credit. Habit.",
    },
    {
      id: "CC-21",
      name: "Hugo Mendel",
      age: 28,
      visit_type: "New Applicant",
      device: "Mobile",
      completion_time: "Approved (No Activation)",
      sentiment: "positive",
      quote:
        "Loved the form, but the speed kind of made me want to slow down before activating. Wanted to think about it.",
    },
    {
      id: "CC-22",
      name: "Sasha Voronin",
      age: 38,
      visit_type: "Existing Customer",
      device: "Mobile",
      completion_time: "Approved (No Activation)",
      sentiment: "neutral",
      quote:
        "I think I'm going to wait. The whole thing felt like it deserved more friction at the end. Like a 'are you sure?' moment.",
    },
    {
      id: "CC-23",
      name: "Wendell Booker",
      age: 62,
      visit_type: "Premier Customer",
      device: "Desktop",
      completion_time: "Approved (No Activation)",
      sentiment: "neutral",
      quote:
        "Approved in minutes, but I want a paper copy of the cardholder agreement before I activate. Old habit.",
    },
    {
      id: "CC-24",
      name: "Maritza Velez",
      age: 30,
      visit_type: "New Applicant",
      device: "Mobile",
      completion_time: "Approved (No Activation)",
      sentiment: "positive",
      quote:
        "Form was beautiful. I just want to talk to my husband before activating a new line of credit.",
    },
    {
      id: "CC-25",
      name: "Drew Halverson",
      age: 26,
      visit_type: "New Applicant",
      device: "Mobile",
      completion_time: "Approved (No Activation)",
      sentiment: "neutral",
      quote:
        "I haven't activated. I think I might forget about it, honestly. The flow was almost too easy to remember.",
    },
    {
      id: "CC-26",
      name: "Glen Pham",
      age: 44,
      visit_type: "Existing Customer",
      device: "Mobile",
      completion_time: "Approved (No Activation)",
      sentiment: "neutral",
      quote:
        "Great form. I'm sitting on it because I want to keep my credit utilization clean.",
    },
    {
      id: "CC-27",
      name: "Yvette Solomon",
      age: 50,
      visit_type: "Premier Customer",
      device: "Desktop",
      completion_time: "Approved (No Activation)",
      sentiment: "negative",
      quote:
        "I finished and was approved but I felt rushed into approval. I'd actually rather a slower flow that lets me feel like I'm committing.",
    },
    {
      id: "CC-28",
      name: "Brian Cathcart",
      age: 37,
      visit_type: "New Applicant",
      device: "Mobile",
      completion_time: "Abandoned at Step 2",
      sentiment: "negative",
      quote:
        "When my income just appeared, I bailed. I have no idea where Chase pulled that from. Felt creepy.",
    },
    {
      id: "CC-29",
      name: "Cassandra Ofori",
      age: 34,
      visit_type: "Existing Customer",
      device: "Mobile",
      completion_time: "Abandoned at Step 2",
      sentiment: "negative",
      quote:
        "The income wasn't even right — it showed last year's, not this year's. I gave up.",
    },
    {
      id: "CC-30",
      name: "Doug Richter",
      age: 60,
      visit_type: "Premier Customer",
      device: "Desktop",
      completion_time: "Abandoned at Step 2",
      sentiment: "negative",
      quote:
        "Auto-population without explanation is a non-starter for someone in my generation. Tell me what you're using.",
    },
    {
      id: "CC-31",
      name: "Iris Boateng",
      age: 29,
      visit_type: "New Applicant",
      device: "Mobile",
      completion_time: "Abandoned at Step 2",
      sentiment: "negative",
      quote:
        "I closed the tab when the verification page just had my SSN already partly filled. I didn't grant that.",
    },
    {
      id: "CC-32",
      name: "Mauricio Suri",
      age: 42,
      visit_type: "Existing Customer",
      device: "Mobile",
      completion_time: "Abandoned at Step 2",
      sentiment: "neutral",
      quote:
        "I love how short the form was, but I stopped at the income step. I needed to know how Chase 'knew.'",
    },
    {
      id: "CC-33",
      name: "Tessa Friedman",
      age: 25,
      visit_type: "New Applicant",
      device: "Mobile",
      completion_time: "Abandoned at Step 2",
      sentiment: "negative",
      quote:
        "It assumed I made $94k. I make $52k. I worried I was about to take a credit line I couldn't pay.",
    },
    {
      id: "CC-34",
      name: "Dion Castello",
      age: 48,
      visit_type: "Existing Customer",
      device: "Desktop",
      completion_time: "Abandoned at Step 2",
      sentiment: "neutral",
      quote:
        "I would have preferred a manual entry option, even if slower. Speed isn't the only thing I value.",
    },
    {
      id: "CC-35",
      name: "Anaya Pillai",
      age: 33,
      visit_type: "New Applicant",
      device: "Mobile",
      completion_time: "Abandoned at Step 2",
      sentiment: "negative",
      quote:
        "I have anxiety about credit and the auto-fill made me feel like I'd lost control of the application.",
    },
    {
      id: "CC-36",
      name: "Kelvin Yoon",
      age: 41,
      visit_type: "Existing Customer",
      device: "Mobile",
      completion_time: "Abandoned at Step 3",
      sentiment: "neutral",
      quote:
        "Offer popped up a little too fast. I hadn't given enough info to feel like the offer was real.",
    },
    {
      id: "CC-37",
      name: "Helena Kovacs",
      age: 56,
      visit_type: "Premier Customer",
      device: "Desktop",
      completion_time: "Abandoned at Step 3",
      sentiment: "neutral",
      quote:
        "$45k offer in 90 seconds. That's not a serious offer. I'd rather Chase took 5 more minutes to make me feel it was earned.",
    },
    {
      id: "CC-38",
      name: "Roberto Esparza",
      age: 31,
      visit_type: "New Applicant",
      device: "Mobile",
      completion_time: "Abandoned at Step 3",
      sentiment: "positive",
      quote:
        "I loved the offer screen but there was no way to save it for later. I wanted time to think.",
    },
    {
      id: "CC-39",
      name: "Margot Sealey",
      age: 67,
      visit_type: "Premier Customer",
      device: "Desktop",
      completion_time: "Abandoned at Step 4",
      sentiment: "neutral",
      quote:
        "I expanded all the collapsible sections and got overwhelmed. Maybe a 'plain English' summary in addition would help.",
    },
    {
      id: "CC-40",
      name: "Cooper Yates",
      age: 39,
      visit_type: "Existing Customer",
      device: "Mobile",
      completion_time: "Abandoned at Step 4",
      sentiment: "negative",
      quote:
        "The single checkbox at the end made me feel like I was being railroaded into agreeing without reading. I closed it.",
    },
    {
      id: "CC-41",
      name: "Amber Hutchinson",
      age: 35,
      visit_type: "New Applicant",
      device: "Mobile",
      completion_time: "Abandoned at Step 4",
      sentiment: "neutral",
      quote:
        "The term highlights were good but I wanted to download the full agreement before signing. Couldn't find it.",
    },
    {
      id: "CC-42",
      name: "Vince Calabrese",
      age: 52,
      visit_type: "Premier Customer",
      device: "Desktop",
      completion_time: "Abandoned at Step 4",
      sentiment: "negative",
      quote:
        "I'm a fiduciary by training. I will not consent without reading the legal text in full. The collapse design felt like an attempt to discourage that.",
    },
    {
      id: "CC-43",
      name: "Stefan Liu",
      age: 43,
      visit_type: "New Applicant",
      device: "Mobile",
      completion_time: "Declined (Soft Pull)",
      sentiment: "positive",
      quote:
        "Declined cleanly with a one-page explanation. No drama, didn't waste my time. That part actually felt respectful.",
    },
    {
      id: "CC-44",
      name: "Bridget O'Hara",
      age: 36,
      visit_type: "New Applicant",
      device: "Mobile",
      completion_time: "Declined (Soft Pull)",
      sentiment: "neutral",
      quote:
        "Declined. The decline screen was actually nicer than the approval screens at some other banks I've been turned down at.",
    },
  ];

  const experimentDetailFallback: ExperimentDetail = {
    id: "exp-3",
    name: CREDIT_CARD_NAME,
    goal: "Reduce credit card application completion time by 60% and increase approval-to-activation conversion rate by 25% through a simplified digital-first application experience.",
    context:
      "Premium cardholders experience significant friction during credit card application upgrades — our current 12-step process causes 40% drop-off rates and average completion times exceeding 25 minutes. Recent customer feedback surveys and declining Q3 conversion rates make solving this a priority. We're testing a streamlined 5-step flow with real-time document verification and instant pre-qualification feedback.",
    audience:
      "Premium and prospective cardholders\nExisting Chase Sapphire / Freedom / Ink customers\nMobile-first applicants",
    owner: "Sarah Chen",
    type: 2,
    status: "completed",
    createdAt: new Date(Date.now() - 1000 * 60 * 60 * 24 * 21).toISOString(),
    updatedAt: new Date(Date.now() - 1000 * 60 * 60).toISOString(),
    foundation: {
      objective:
        "Validate that a simplified 5-step credit card application flow will reduce abandonment by 40% and increase completion rates compared to the current 12-step process.",
      hypothesis:
        "If we reduce the credit card application steps from 12 to 5 with real-time document verification, then completion rates will increase by 40% and average completion time will drop below 5 minutes, because users abandon due to perceived complexity, excessive time investment, and uncertainty about approval status.",
    },
    premises: [
      {
        id: "rq1",
        type: 1,
        value:
          "Will users trust the new auto-populated income verification from connected bank accounts?",
      },
      {
        id: "rq2",
        type: 1,
        value:
          "Does the simplified flow reduce cognitive load and time-to-completion as expected?",
      },
      {
        id: "rq3",
        type: 1,
        value:
          "Can users complete the new streamlined flow in under 5 minutes with fewer errors?",
      },
      {
        id: "a1",
        type: 2,
        value:
          "Users will trust auto-populated income data from connected Chase accounts.",
      },
      {
        id: "a2",
        type: 2,
        value:
          "Reducing steps won't compromise data quality or regulatory compliance requirements.",
      },
      {
        id: "a3",
        type: 2,
        value:
          "Mobile users will benefit equally from the streamlined desktop-first flow.",
      },
      {
        id: "s1",
        type: 3,
        value:
          "Users express confidence in the new process; positive sentiment in post-task interviews; users complete flow without backtracking or confusion.",
      },
      {
        id: "s2",
        type: 3,
        value:
          "Completion rate exceeds 60% (up from 35%); average time under 5 minutes; error rate below 5%; NPS increase of 15+ points.",
      },
    ],
    okrs: [
      {
        id: "okr-c1",
        type: 1,
        okr: "Increase annual recurring revenue by 30% through product-led growth initiatives.",
      },
      {
        id: "okr-c2",
        type: 1,
        okr: "Reduce customer acquisition cost by 20% while maintaining conversion quality.",
      },
      {
        id: "okr-t1",
        type: 2,
        okr: "Drive NPS >70 and increase premium user activation by delivering personalized, high-trust flows.",
      },
      {
        id: "okr-t2",
        type: 2,
        okr: "Reduce customer acquisition cost by 15% while improving conversion rates through optimized user experiences.",
      },
    ],
    attachments: [],
  };

  return {
    insightsData,
    metricsOverride: {
      completion_rate: 62,
      average_time_to_complete: "4m 11s",
    },
    decisionSupportingResponses,
    interviewQuestionsByRqId,
    participants,
    participantSegmentLabel: "Customer Type",
    participantTimeLabel: "Outcome",
    experimentDetailFallback,
  };
}
